import { z } from 'zod';
import { IdSchema, E164Schema, TimestampSchema, ListQuerySchema } from './common.js';

export const CarrierSchema = z.object({
  id: IdSchema,
  name: z.string(),
  status: z.enum(['active', 'disabled']),
  rate_deck_id: IdSchema.nullable(),
  trunk_count: z.number().int().optional(),
  created_at: TimestampSchema,
  version: z.number().int(),
});
export type Carrier = z.infer<typeof CarrierSchema>;

export const CarrierCreateSchema = z.object({
  name: z.string().min(1).max(120),
  status: z.enum(['active', 'disabled']).default('active'),
}).strict();

export const TrunkDirectionSchema = z.enum(['inbound', 'outbound', 'both']);
export const TrunkStatusSchema = z.enum(['active', 'disabled', 'draining']);
export const TransportSchema = z.enum(['udp', 'tcp', 'tls']);
/**
 * 'ip': the carrier trusts our address. 'digest': we answer its challenge on
 * outbound calls. 'register': digest plus a registration, so the carrier knows
 * where to send inbound calls. Inbound is recognised by source_ips in all three.
 */
export const AuthModeSchema = z.enum(['ip', 'digest', 'register']);

/**
 * Identity presentation and withholding, mirroring the CHECK constraints on
 * `trunks` (migration 0010).
 *
 * Declared once because the same two lists were written out four times and
 * drifted: 'pai_only' and 'full' were accepted here and offered by the trunk
 * editor, the table refused them, and the save came back a bare 500.
 *
 * Both are implemented as of September 2026, in that order: the outbound bridge
 * builds the headers (carrierIdentity in apps/agent/src/fsxml.ts), `privacy_mode`
 * is carried to the node, and the dialplan selects by mode -- `pid` asserts the
 * identity, `rpid` sends Remote-Party-ID, `from_only` sends neither and builds
 * the Privacy header itself because mod_sofia writes it only on the pid path.
 *
 * 'header' is accepted and behaves as 'id'. RFC 3323's header privacy is about
 * obscuring topology, not withholding a number, and mod_sofia builds the value
 * from flags we cannot append to -- so the portal labels it for what it does
 * rather than for what it is called. Adding a mode still means building the
 * header in the dialplan FIRST, then the constraint, then this.
 */
export const CliModeSchema = z.enum(['from_only', 'from_and_pai', 'rpid']);
export const PrivacyModeSchema = z.enum(['none', 'id', 'header']);

/**
 * What the switch last measured about a trunk: whether the carrier answers an
 * OPTIONS ping from our SIP socket, whether our registration with it holds,
 * and the realm it challenges with. Written on the node by arc-agent
 * (trunkstatus.ts) and laid over the trunk by the API; null where no switch
 * has reported on the trunk (a disabled trunk, or one no customer uses yet).
 */
export const TrunkLiveSchema = z.object({
  /** 'off': pinging disabled, or not possible (TLS, no public address). */
  health: z.enum(['up', 'degraded', 'down', 'unknown', 'off']),
  health_since: TimestampSchema.nullable(),
  /**
   * How long the ping took END TO END through OpenSIPS's management interface,
   * which adds about 0.5 s to any async command. Not the network round trip
   * (Switch Tel answers in under 3 ms), so not shown as one; a jump from 0.5 s
   * to several seconds is still worth noticing.
   */
  rtt_ms: z.number().int().nullable(),
  /** The carrier's reply to the last ping, or what happened instead. */
  last_reply: z.string().nullable(),
  consecutive_failures: z.number().int(),
  last_ping_at: TimestampSchema.nullable(),
  last_reply_at: TimestampSchema.nullable(),
  /**
   * OpenSIPS's registration state: REGISTERED_STATE, WRONG_CREDENTIALS_STATE,
   * REGISTRAR_ERROR_STATE, REGISTER_TIMEOUT_STATE, NOT_REGISTERED_STATE, ...;
   * NOT_LOADED before OpenSIPS has picked the trunk up; null when it does not
   * register at all.
   */
  reg_state: z.string().nullable(),
  reg_expires_sec: z.number().int().nullable(),
  last_register_at: TimestampSchema.nullable(),
  /** The realm the carrier challenges with, when it does. */
  challenge_realm: z.string().nullable(),
  /** When the switch last reported. Old means the switch has stopped reporting. */
  reported_at: TimestampSchema,
  /** Network round trip to the carrier's SIP service, from a direct ping. */
  latency_ms: z.number().nullable().optional(),
  /** Share of health checks answered over the last 24 hours; null with no record yet. */
  uptime_24h_pct: z.number().nullable().optional(),
});
export type TrunkLive = z.infer<typeof TrunkLiveSchema>;

/**
 * A trunk's record over a window: uptime and latency per bucket, and every
 * change of state. Read from the node (ns_trunk_samples, ns_trunk_events).
 */
export const TrunkHistorySchema = z.object({
  hours: z.number().int(),
  bucket_sec: z.number().int(),
  uptime_pct: z.number().nullable(),
  latency_avg_ms: z.number().nullable(),
  latency_p95_ms: z.number().nullable(),
  buckets: z.array(z.object({
    at: TimestampSchema,
    /** Share of checks answered in the bucket; null when there were none. */
    up_pct: z.number().nullable(),
    latency_ms: z.number().nullable(),
    registered: z.boolean().nullable(),
  })),
  events: z.array(z.object({
    at: TimestampSchema,
    kind: z.enum(['health', 'registration']),
    from_state: z.string().nullable(),
    to_state: z.string(),
    /** Staff only: the carrier's reply or the switch's reason. */
    detail: z.string().nullable().optional(),
  })),
});
export type TrunkHistory = z.infer<typeof TrunkHistorySchema>;

/**
 * Note the absence of auth_secret_enc / auth_secret_nonce.
 *
 * A trunk password is a credential to someone else's network -- leaking one is
 * both a security incident and a commercial one. It is never joined into this
 * projection, so schema-driven serialisation cannot emit it even by mistake.
 * `auth_secret_set` reports only whether one exists.
 */
export const TrunkSchema = z.object({
  id: IdSchema,
  /** Null for a customer's own trunk: its carrier is not one of ours. */
  carrier_id: IdSchema.nullable(),
  carrier_name: z.string().optional(),
  /** The owning account, for a customer's own trunk (BYOT); null for ours. */
  tenant_id: IdSchema.nullable().optional(),
  /** A customer's own carrier, as the customer named it. */
  provider_name: z.string().nullable().optional(),
  name: z.string(),
  direction: TrunkDirectionSchema,
  status: TrunkStatusSchema,

  host: z.string(),
  port: z.number().int(),
  transport: TransportSchema,
  use_srv: z.boolean(),
  source_ips: z.array(z.string()),

  auth_mode: AuthModeSchema,
  auth_username: z.string().nullable(),
  auth_realm: z.string().nullable(),
  auth_secret_set: z.boolean(),
  register_expiry_sec: z.number().int(),

  max_concurrent_calls: z.number().int().nullable(),
  max_cps: z.number().int().nullable(),

  outbound_format: z.enum(['e164', 'e164_no_plus', 'national', '00_prefixed']),
  outbound_prefix: z.string().nullable(),
  inbound_strip: z.number().int(),
  inbound_prepend: z.string().nullable(),

  cli_mode: CliModeSchema,
  fallback_cli: E164Schema.nullable(),
  privacy_mode: PrivacyModeSchema,

  allowed_codecs: z.array(z.string()),
  dtmf_mode: z.enum(['rfc2833', 'info', 'inband']),
  t38_enabled: z.boolean(),
  early_media: z.boolean(),
  // Present in the schema since it was written, never exposed for reading, so
  // nothing could show or change them.
  session_timers: z.boolean(),
  rel100: z.boolean(),
  options_ping_sec: z.number().int(),
  failure_threshold: z.number().int(),

  health_status: z.enum(['unknown', 'up', 'down', 'degraded']),
  last_health_change: TimestampSchema.nullable(),
  /** Live state from the switch; see TrunkLiveSchema. Absent on write responses. */
  live: TrunkLiveSchema.nullable().optional(),

  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  version: z.number().int(),
});
export type Trunk = z.infer<typeof TrunkSchema>;

const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;

/**
 * Values the switch splices into a dial string ({sip_invite_domain=REALM}sofia/internal/N@HOST:PORT),
 * where , { } [ ] | @ and quotes have meaning: one in a host or realm could add channel variables or
 * whole bridge legs to every outbound call (security review, 21 October 2026). A hostname or IPv4
 * for the host, the same charset for the realm, and SIP token characters for the username -- as the
 * customer's own-trunk path already requires.
 */
const DIAL_HOST = /^(?=.{1,253}$)(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}|(?:\d{1,3}\.){3}\d{1,3})$/i;
const SIP_USER = /^[A-Za-z0-9._~+\-!*'()%$&=;?/]+$/;

const TrunkCreateBase = z.object({
  carrier_id: IdSchema,
  name: z.string().min(1).max(120),
  direction: TrunkDirectionSchema.default('both'),
  host: z.string().trim().min(1).max(253).regex(DIAL_HOST, 'must be a hostname or an IPv4 address'),
  port: z.number().int().min(1).max(65535).default(5060),
  transport: TransportSchema.default('udp'),
  use_srv: z.boolean().default(false),
  source_ips: z.array(z.string().regex(ipv4, 'must be an IPv4 address or CIDR')).default([]),

  auth_mode: AuthModeSchema.default('ip'),
  auth_username: z.string().max(120).regex(SIP_USER, 'may not contain spaces, commas, quotes, brackets, | or @').or(z.literal('')).optional(),
  auth_realm: z.string().trim().max(253).regex(DIAL_HOST, 'must be a hostname or an IPv4 address').or(z.literal('')).optional(),
  auth_secret: z.string().min(1).max(256).optional(),
  register_expiry_sec: z.number().int().min(60).max(86400).default(3600),

  max_concurrent_calls: z.number().int().min(1).max(10000).optional(),
  max_cps: z.number().int().min(1).max(1000).optional(),

  outbound_format: z.enum(['e164', 'e164_no_plus', 'national', '00_prefixed']).default('e164'),
  outbound_prefix: z.string().max(16).optional(),
  inbound_strip: z.number().int().min(0).max(20).default(0),
  inbound_prepend: z.string().max(16).optional(),

  cli_mode: CliModeSchema.default('from_and_pai'),
  fallback_cli: E164Schema.optional(),
  allowed_codecs: z.array(z.string().max(24)).default(['PCMA', 'PCMU']),
  dtmf_mode: z.enum(['rfc2833', 'info', 'inband']).default('rfc2833'),
  t38_enabled: z.boolean().default(false),
});

/**
 * An IP-authenticated trunk with no source_ips accepts calls from nobody --
 * or, if the ACL is ever applied permissively by mistake, from everybody.
 * Refusing it here means an open inbound trunk cannot be created by omission.
 */
const inboundNeedsSources = (t: { auth_mode: string; direction: string; source_ips: string[] }) =>
  t.auth_mode !== 'ip' || t.direction === 'outbound' || t.source_ips.length > 0;
const digestNeedsCredentials = (t: { auth_mode: string; auth_username?: string | undefined; auth_secret?: string | undefined }) =>
  t.auth_mode === 'ip' || (!!t.auth_username && !!t.auth_secret);

export const TrunkCreateSchema = TrunkCreateBase
  .strict()
  .refine(inboundNeedsSources, {
    message: 'An inbound IP-authenticated trunk must list at least one source IP',
    path: ['source_ips'],
  })
  .refine(digestNeedsCredentials, {
    message: 'Digest and registration trunks need a username and a password',
    path: ['auth_secret'],
  });
export type TrunkCreate = z.infer<typeof TrunkCreateSchema>;

/**
 * Whether an account's calls use OUR trunks or its OWN (bring your own trunk).
 * The provider's choice, made in the admin portal.
 */
export const TrunkModeSchema = z.enum(['arcnet', 'byot']);
export const TrunkModeUpdateSchema = z.object({ trunk_mode: TrunkModeSchema }).strict();

/**
 * A customer's own trunk (BYOT), created from its own portal.
 *
 * Differs from one of ours in three ways. It names its provider rather than
 * choosing one of our carriers. It has no inbound strip/prepend: several
 * customers may use the same carrier, whose calls arrive from the same
 * addresses, and one customer's number rewriting must not be able to reach
 * another's calls -- the switch accepts a number in international, 00 or
 * national form instead. And its host and addresses are checked server-side
 * for being public (see the API): the switch will send to whatever it names.
 */
export const OwnTrunkCreateSchema = TrunkCreateBase
  .omit({ carrier_id: true, inbound_strip: true, inbound_prepend: true })
  .extend({ provider_name: z.string().min(1).max(120) })
  .strict()
  .refine(inboundNeedsSources, {
    message: 'An inbound IP-authenticated trunk must list at least one source IP',
    path: ['source_ips'],
  })
  .refine(digestNeedsCredentials, {
    message: 'Digest and registration trunks need a username and a password',
    path: ['auth_secret'],
  });
export type OwnTrunkCreate = z.infer<typeof OwnTrunkCreateSchema>;

export const TrunkUpdateSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  direction: TrunkDirectionSchema.optional(),
  status: TrunkStatusSchema.optional(),
  host: z.string().trim().min(1).max(253).regex(DIAL_HOST, 'must be a hostname or an IPv4 address').optional(),
  port: z.number().int().min(1).max(65535).optional(),
  transport: TransportSchema.optional(),
  source_ips: z.array(z.string().regex(ipv4)).optional(),
  max_concurrent_calls: z.number().int().min(1).max(10000).nullable().optional(),
  max_cps: z.number().int().min(1).max(1000).nullable().optional(),
  outbound_prefix: z.string().max(16).nullable().optional(),
  inbound_strip: z.number().int().min(0).max(20).optional(),
  inbound_prepend: z.string().max(16).nullable().optional(),
  fallback_cli: E164Schema.nullable().optional(),
  allowed_codecs: z.array(z.string().max(24)).optional(),
  dtmf_mode: z.enum(['rfc2833', 'info', 'inband']).optional(),
  t38_enabled: z.boolean().optional(),

  /**
   * The settings a carrier dictates and nothing else can guess.
   *
   * All of these existed on the trunk record and none could be changed through
   * the API, so a new carrier could be created but not configured to match
   * what it actually expects. Getting `outbound_format` wrong alone means
   * every call fails at the far end with an opaque 404.
   */
  outbound_format: z.enum(['e164', 'e164_no_plus', 'national', '00_prefixed']).optional(),
  use_srv: z.boolean().optional(),
  cli_mode: CliModeSchema.optional(),
  privacy_mode: PrivacyModeSchema.optional(),
  early_media: z.boolean().optional(),
  session_timers: z.boolean().optional(),
  rel100: z.boolean().optional(),
  /** 0 disables probing. A carrier that objects to OPTIONS will say so. */
  options_ping_sec: z.number().int().min(0).max(3600).optional(),
  failure_threshold: z.number().int().min(1).max(100).optional(),

  /**
   * Credentials. auth_secret is WRITE-ONLY: sealed on the way in and never
   * returned. Leave it out to keep the stored password; send it to rotate.
   */
  auth_mode: AuthModeSchema.optional(),
  auth_username: z.string().max(120).regex(SIP_USER, 'may not contain spaces, commas, quotes, brackets, | or @').or(z.literal('')).nullable().optional(),
  auth_realm: z.string().trim().max(253).regex(DIAL_HOST, 'must be a hostname or an IPv4 address').or(z.literal('')).nullable().optional(),
  auth_secret: z.string().min(1).max(256).optional(),
  register_expiry_sec: z.number().int().min(60).max(86400).optional(),
}).strict();

/** Changes to a customer's own trunk: as ours, minus inbound rewriting (see create). */
export const OwnTrunkUpdateSchema = TrunkUpdateSchema
  .omit({ inbound_strip: true, inbound_prepend: true })
  .extend({ provider_name: z.string().min(1).max(120).optional() })
  .strict();

export const TrunkListQuerySchema = ListQuerySchema.extend({
  carrier_id: IdSchema.optional(),
  status: TrunkStatusSchema.optional(),
  q: z.string().max(200).optional(),
});

/**
 * Number format tester (doc 07 section 4.11).
 *
 * Validates a trunk's transformation rules against real examples BEFORE it can
 * carry traffic. Getting these wrong produces calls that fail at the carrier
 * with an opaque 404, which is miserable to debug from SIP traces alone.
 */
export const NumberFormatTestSchema = z.object({
  numbers: z.array(z.string().min(1).max(24)).min(1).max(20),
  direction: z.enum(['inbound', 'outbound']).default('outbound'),
}).strict();

export const NumberFormatResultSchema = z.object({
  results: z.array(z.object({
    input: z.string(),
    output: z.string(),
    note: z.string().nullable(),
  })),
});
